import React, { FC, Fragment, useEffect } from 'react'
import { hot } from 'react-hot-loader/root'
import { IntercomProvider } from 'react-use-intercom'
import { ThemeProvider } from 'styled-components'
import { ParallaxProvider } from 'react-scroll-parallax'
import { useDispatch, useSelector } from 'react-redux'

import { identifySegment } from '@app/utils/analytics'
import theme from '@app/ui/theme'
import GlobalStyles from '@app/ui/theme/globalStyles'
import { Meta } from '@ui/blocks/Meta'
import { NotificationsContainer } from '@ui/blocks/Notifications'
import { CTMScript } from '@ui/blocks/CTMScript'
import { getSessionIdSelector } from '@app/store/session'
import { actions, testVariantIdLocationFilterSelector, TestVariantsIdsLocationFilter } from '@app/store/booking'
import { generateRandomInteger } from '@app/utils/generateRandomInteger'
import { runtimeConfig } from '@app/common/config'

import { appRoutes } from './common/routes'
import { renderRouting } from './common/routing'

const Pages: FC = ({ children }) => {
  const dispatch = useDispatch()

  const sessionId = useSelector(getSessionIdSelector)
  const experimentIdLocationFilter = runtimeConfig.joinSproutTherapyConfig?.GO_EXPERIMENT_ID_LOCATION_FILTER
  const testVariantIdLocationFilter = useSelector(testVariantIdLocationFilterSelector)

  useEffect(() => {
    if (sessionId) {
      identifySegment(sessionId)
    }
  }, [sessionId])

  useEffect(() => {
    if (experimentIdLocationFilter && testVariantIdLocationFilter === TestVariantsIdsLocationFilter.Undetermined) {
      dispatch(
        actions.setTestVariantIdLocationFilter(
          generateRandomInteger(TestVariantsIdsLocationFilter.Control, TestVariantsIdsLocationFilter.Test)
        )
      )
    }
  }, [dispatch, experimentIdLocationFilter, testVariantIdLocationFilter])

  return <>{children}</>
}

export const App = hot(() => {
  const intercomAppId = runtimeConfig.joinSproutTherapyConfig?.INTERCOM_ID
  const googleTagManagerId = runtimeConfig.joinSproutTherapyConfig?.GTM_ID

  return (
    <ThemeProvider theme={theme}>
      <IntercomProvider appId={intercomAppId} autoBoot initializeDelay={5000}>
        <Meta
          title="Elemy | Autism Care for All"
          description="Elemy is a tech-forward provider of Pediatric & ABA Therapy. Our Mission is to provide the highly personalized care in an environment most suited to help children with their unique needs."
        />
        <noscript>
          <iframe
            title="Google Tag Manager (noscript)"
            src={`https://www.googletagmanager.com/ns.html?id=${googleTagManagerId}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>

        <Fragment>
          <GlobalStyles />
          <CTMScript />
          <ParallaxProvider>
            <Pages>{renderRouting(appRoutes)}</Pages>
          </ParallaxProvider>
          <NotificationsContainer />
        </Fragment>
      </IntercomProvider>
    </ThemeProvider>
  )
})
